import { Chip, Grid, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import { VisitorGroupMembership } from 'graphql/generated/graphqlTypes';
import useVisitorDetails from 'hooks/allVisitors/pages/userDetails/useVisitorDetails';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import React from 'react';
import {
  getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers,
  getUserDeletionDate,
  translateRole
} from 'utils/Visitor/visitor';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import EditableCard from 'v2/components/EditableCard/EditableCard';
import InfoGrid from 'v2/components/InfoGrid/InfoGrid';
import Loader from 'v2/components/Loader/Loader';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import UserFormDrawer from 'v2/pages/Users/Drawers/UserFormDrawer/UserFormDrawer';
import { formatDate } from 'v2/utils/dateTime';
import { getVisitorOrigin, getVisitorOriginLabel, VisitorOrigin } from 'v2/utils/visitorOrigin';

interface Props {
  visitorId: string;
  visitorIdentityProvider: string;
  visitorExpirationDate: Dayjs | undefined;
  visitorVisitorGroupMembership: VisitorGroupMembership[];
}

const getVisitorOriginTooltipBody = (origin: VisitorOrigin, translation: Translation) => {
  switch (origin) {
    case VisitorOrigin.EntraId:
      return translation.user_origin_entra_tooltip;
    case VisitorOrigin.Atlas:
      return translation.user_origin_manual_hint;
    case VisitorOrigin.KeySharing:
      return translation.user_origin_key_manager_hint;
    case VisitorOrigin.Unknown:
      return undefined;
  }
};

const getVisitorOriginTooltipContent = (origin: VisitorOrigin, translation: Translation) => (
  <Grid container direction="row" rowSpacing={1}>
    <Typography variant="body2">{getVisitorOriginTooltipBody(origin, translation)}</Typography>
  </Grid>
);

const UserInfoSection: React.FC<Props> = ({ visitorId }) => {
  const { translation } = useTranslation();
  const { openDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const { visitorDetails, loadingVisitorDetails } = useVisitorDetails({
    translation,
    handleFetchError,
    visitorId
  });
  const handleEditUserClick = () => {
    if (!loadingVisitorDetails && visitorDetails) openDrawer(<UserFormDrawer visitor={visitorDetails} />);
  };

  return (
    <EditableCard
      title={translation.user_info}
      editButtonId="editVisitor"
      onEditClick={handleEditUserClick}
      spacing={2}
    >
      {!loadingVisitorDetails && visitorDetails ? (
        <>
          <InfoGrid equalColumns>
            <InfoGrid.Label
              tooltip={
                getVisitorOrigin(visitorDetails) !== VisitorOrigin.Unknown
                  ? getVisitorOriginTooltipContent(getVisitorOrigin(visitorDetails), translation)
                  : undefined
              }
            >
              {translation.origin}
            </InfoGrid.Label>
            <InfoGrid.Text>{getVisitorOriginLabel(visitorDetails, translation)}</InfoGrid.Text>

            {getUserDeletionDate(visitorDetails) !== null && (
              <>
                <InfoGrid.Label
                  tooltip={
                    <Grid container>
                      <Typography variant="body2">{translation.user_deletion_tooltip}</Typography>
                    </Grid>
                  }
                >
                  {translation.deletion_after}
                </InfoGrid.Label>
                <InfoGrid.Text>{formatDate(getUserDeletionDate(visitorDetails)!)}</InfoGrid.Text>
              </>
            )}
          </InfoGrid>
          <Grid container spacing={1}>
            {getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers(visitorDetails).map((role) => (
              <Chip
                variant="filled"
                size="small"
                sx={{ width: 'fit-content' }}
                label={translateRole(role, translation)}
                key={role}
              />
            ))}
          </Grid>
        </>
      ) : (
        <Loader />
      )}
    </EditableCard>
  );
};

export default UserInfoSection;
